/**
 * Recorrido de un pedido en cuatro pasos, para la linea de estado del modal
 * (ADM-09): creado, pagado, tomado por Random y con documento.
 *
 * No hay paso de despacho: el sitio no sabe cuando sale un pedido (lo sabe
 * Random), y un paso que nunca se completa dice que algo falta cuando no.
 */
import type { PedidoAdminDetalle } from '@elcarpintero/compartido';

/** `actual` es el paso que se esta esperando; `fallido`, el que corto el recorrido. */
export type EstadoPaso = 'hecho' | 'actual' | 'fallido' | 'pendiente';

export interface PasoPedido {
  nombre: string;
  estado: EstadoPaso;
  /** Cuando paso, si paso. */
  fecha: string | null;
  /** Lo que se dice debajo cuando no hay fecha. */
  detalle: string;
}

function pasoPago(pedido: PedidoAdminDetalle): PasoPedido {
  const nombre = 'Pagado';
  if (pedido.estado === 'pagado') {
    return { nombre, estado: 'hecho', fecha: pedido.pagadoEn, detalle: 'Pagado' };
  }
  if (pedido.estado === 'rechazado') {
    return { nombre: 'Pago rechazado', estado: 'fallido', fecha: null, detalle: 'Webpay lo rechazó' };
  }
  if (pedido.estado === 'anulado') {
    return { nombre: 'Anulado', estado: 'fallido', fecha: null, detalle: 'El cliente no pagó' };
  }
  return { nombre, estado: 'actual', fecha: null, detalle: 'Sin pagar' };
}

function pasoRandom(pedido: PedidoAdminDetalle): PasoPedido {
  const nombre = 'Nota de venta';
  if (pedido.random.registradoEn !== null) {
    return { nombre, estado: 'hecho', fecha: pedido.random.registradoEn, detalle: 'Tomado por Random' };
  }
  if (pedido.estado !== 'pagado') {
    return { nombre, estado: 'pendiente', fecha: null, detalle: 'No corresponde' };
  }
  if (pedido.estadoSincronizacionRandom === 'sin_tomar') {
    return { nombre, estado: 'fallido', fecha: null, detalle: 'No llegó a Random' };
  }
  return { nombre, estado: 'actual', fecha: null, detalle: 'Esperando a Random' };
}

function pasoDocumento(pedido: PedidoAdminDetalle, anterior: PasoPedido): PasoPedido {
  const nombre = pedido.tipoDocumento === 'factura' ? 'Facturado' : 'Boleta emitida';
  if (pedido.folioDocumento !== null) {
    return { nombre, estado: 'hecho', fecha: pedido.random.fechaDocumento, detalle: 'Emitido' };
  }
  if (anterior.estado === 'hecho') {
    return { nombre, estado: 'actual', fecha: null, detalle: 'Esperando el folio' };
  }
  return { nombre, estado: 'pendiente', fecha: null, detalle: 'Pendiente' };
}

export function pasosPedido(pedido: PedidoAdminDetalle): PasoPedido[] {
  const random = pasoRandom(pedido);
  return [
    { nombre: 'Creado', estado: 'hecho', fecha: pedido.creadoEn, detalle: 'Creado' },
    pasoPago(pedido),
    random,
    pasoDocumento(pedido, random),
  ];
}
